/**
 * Configuration export service
 *
 * Exports DatacenterConfig to various formats:
 * - JSON: Raw configuration data
 * - SysML: SysML 2.0 textual notation
 * - Ansible: Ansible inventory YAML
 * - WireGuard: VPN configuration file
 * - BOM: Bill of materials CSV
 */

import { DatacenterConfig, DatacenterNode, VPNPeer } from '../types/configurator';

export type ExportFormat = 'json' | 'sysml' | 'ansible' | 'wireguard' | 'bom';

/**
 * Export configuration to specified format
 */
export function exportConfig(config: DatacenterConfig, format: ExportFormat): string {
  switch (format) {
    case 'json':
      return exportJSON(config);
    case 'sysml':
      return exportSysML(config);
    case 'ansible':
      return exportAnsible(config);
    case 'wireguard':
      return exportWireGuard(config);
    case 'bom':
      return exportBOM(config);
    default:
      throw new Error(`Unknown export format: ${format}`);
  }
}

/**
 * Download a file to the user's computer
 */
export function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export as JSON
 */
function exportJSON(config: DatacenterConfig): string {
  return JSON.stringify(config, null, 2);
}

/**
 * Export as SysML 2.0 textual notation
 */
function exportSysML(config: DatacenterConfig): string {
  const safeName = config.name.replace(/[^a-zA-Z0-9]/g, '_') || 'Datacenter';
  const timestamp = new Date().toISOString();

  let sysml = `// Generated by hermit-μDC Configurator
// ${timestamp}

package ${safeName}Config {
  doc /* ${config.description || 'Micro-datacenter configuration'} */

  // Network Configuration
  part def NetworkConfig {
    attribute subnet : String = "${config.network.subnet}";
    attribute gateway : String = "${config.network.gateway}";
    attribute netmask : String = "${config.network.netmask}";
    attribute domain : String = "${config.network.domain}";
    attribute dhcpRangeStart : String = "${config.network.dhcpRangeStart}";
    attribute dhcpRangeEnd : String = "${config.network.dhcpRangeEnd}";
  }

`;

  // Add node definitions
  if (config.nodes.length > 0) {
    sysml += `  // Node Definitions\n`;
    for (const node of config.nodes) {
      sysml += `  part def ${sanitizeName(node.name)} :> DatacenterNode {
    attribute hostname : String = "${node.hostname}";
    attribute nodeType : String = "${node.type}";
    attribute ip : String = "${node.ip}";
    attribute cpuCores : Integer = ${node.specs.cpuCores};
    attribute memoryGB : Integer = ${node.specs.memoryGB};
    attribute storageGB : Integer = ${node.specs.storageGB};
    attribute powerWatts : Integer = ${node.specs.powerWatts};
    attribute enabled : Boolean = ${node.enabled};
  }

`;
    }
  }

  // Add security configuration
  sysml += `  // Security Configuration
  part def SecurityConfig {
    attribute vpnEnabled : Boolean = ${config.security.vpnEnabled};
    attribute vpnListenPort : Integer = ${config.security.vpnListenPort};
    attribute vpnSubnet : String = "${config.security.vpnSubnet}";
    attribute encryptStorage : Boolean = ${config.security.encryptStorage};
    attribute auditLogging : Boolean = ${config.security.auditLogging};
    attribute firewallDefaultDeny : Boolean = ${config.security.firewallDefaultDeny};
  }

`;

  // Add computed values
  sysml += `  // Computed Values
  attribute estimatedCost : Real = ${config.estimatedCost.toFixed(2)};
  attribute estimatedPower : Integer = ${config.estimatedPower};
}
`;

  return sysml;
}

/**
 * Export as Ansible inventory YAML
 */
function exportAnsible(config: DatacenterConfig): string {
  const groups: Record<string, DatacenterNode[]> = {
    gateways: [],
    compute: [],
    storage: [],
    daq: [],
  };

  for (const node of config.nodes.filter(n => n.enabled)) {
    switch (node.type) {
      case 'gateway':
        groups.gateways.push(node);
        break;
      case 'compute':
        groups.compute.push(node);
        break;
      case 'storage':
        groups.storage.push(node);
        break;
      case 'daq':
        groups.daq.push(node);
        break;
    }
  }

  let yaml = `# Ansible Inventory for ${config.name || 'hermit-μDC'}
# Generated by hermit-μDC Configurator
# ${new Date().toISOString()}

all:
  vars:
    ansible_user: hermit
    ansible_become: true
    domain: ${config.network.domain}
    subnet: ${config.network.subnet}
    gateway: ${config.network.gateway}

  children:
`;

  // Add each group
  for (const [groupName, nodes] of Object.entries(groups)) {
    if (nodes.length > 0) {
      yaml += `    ${groupName}:\n`;
      yaml += `      hosts:\n`;
      for (const node of nodes) {
        yaml += `        ${node.hostname}:\n`;
        yaml += `          ansible_host: ${node.ip}\n`;
        if (node.macAddress) {
          yaml += `          mac_address: ${node.macAddress}\n`;
        }
        yaml += `          cpu_cores: ${node.specs.cpuCores}\n`;
        yaml += `          memory_gb: ${node.specs.memoryGB}\n`;
        yaml += `          storage_gb: ${node.specs.storageGB}\n`;
        if (node.role) {
          yaml += `          role: ${node.role}\n`;
        }
      }
    }
  }

  return yaml;
}

/**
 * Export WireGuard server configuration
 */
function exportWireGuard(config: DatacenterConfig): string {
  if (!config.security.vpnEnabled) {
    return `# WireGuard VPN is disabled in this configuration\n`;
  }

  let wg = `# WireGuard Configuration for ${config.name || 'hermit-μDC'}
# Generated by hermit-μDC Configurator
# ${new Date().toISOString()}

[Interface]
# Server configuration
Address = ${config.security.vpnSubnet.replace('/24', '/32').replace('.0/', '.1/')}
ListenPort = ${config.security.vpnListenPort}
# PrivateKey = <REPLACE_WITH_SERVER_PRIVATE_KEY>
PostUp = iptables -A FORWARD -i %i -j ACCEPT; iptables -A FORWARD -o %i -j ACCEPT
PostDown = iptables -D FORWARD -i %i -j ACCEPT; iptables -D FORWARD -o %i -j ACCEPT

`;

  // Add peer configurations
  const enabledPeers = config.security.peers.filter((p: VPNPeer) => p.enabled);
  if (enabledPeers.length > 0) {
    for (const peer of enabledPeers) {
      wg += `# Peer: ${peer.name}${peer.description ? ` - ${peer.description}` : ''}\n`;
      wg += `[Peer]\n`;
      wg += `PublicKey = ${peer.publicKey || '<REPLACE_WITH_PEER_PUBLIC_KEY>'}\n`;
      wg += `AllowedIPs = ${peer.allowedIPs}\n`;
      wg += `\n`;
    }
  } else {
    wg += `# No VPN peers configured\n`;
  }

  return wg;
}

/**
 * Export Bill of Materials as CSV
 */
function exportBOM(config: DatacenterConfig): string {
  const timestamp = new Date().toISOString().slice(0, 10);
  let csv = `"Item","Type","Hostname","IP Address","CPU Cores","Memory (GB)","Storage (GB)","Power (W)","Status"\n`;

  let itemNum = 1;
  for (const node of config.nodes) {
    csv += `${itemNum},"${node.type}","${node.hostname}","${node.ip}",${node.specs.cpuCores},${node.specs.memoryGB},${node.specs.storageGB},${node.specs.powerWatts},"${node.enabled ? 'Enabled' : 'Disabled'}"\n`;
    itemNum++;
  }

  // Add totals row
  const enabledNodes = config.nodes.filter(n => n.enabled);
  const totalCores = enabledNodes.reduce((sum, n) => sum + n.specs.cpuCores, 0);
  const totalMemory = enabledNodes.reduce((sum, n) => sum + n.specs.memoryGB, 0);
  const totalStorage = enabledNodes.reduce((sum, n) => sum + n.specs.storageGB, 0);
  const totalPower = enabledNodes.reduce((sum, n) => sum + n.specs.powerWatts, 0);

  csv += `\n"TOTALS","${enabledNodes.length} nodes","","",${totalCores},${totalMemory},${totalStorage},${totalPower},""\n`;
  csv += `\n"Estimated Cost","$${config.estimatedCost.toFixed(2)}"\n`;
  csv += `"Generated","${timestamp}"\n`;
  csv += `"Configuration","${config.name || 'Untitled'}"\n`;

  return csv;
}

/**
 * Sanitize a name for use in SysML identifiers
 */
function sanitizeName(name: string): string {
  return name.replace(/[^a-zA-Z0-9]/g, '_').replace(/^[0-9]/, '_$&');
}

/**
 * Get file extension for export format
 */
export function getExportExtension(format: ExportFormat): string {
  switch (format) {
    case 'json':
      return 'json';
    case 'sysml':
      return 'sysml';
    case 'ansible':
      return 'yml';
    case 'wireguard':
      return 'conf';
    case 'bom':
      return 'csv';
    default:
      return 'txt';
  }
}

/**
 * Get MIME type for export format
 */
export function getExportMimeType(format: ExportFormat): string {
  switch (format) {
    case 'json':
      return 'application/json';
    case 'sysml':
      return 'text/plain';
    case 'ansible':
      return 'text/yaml';
    case 'wireguard':
      return 'text/plain';
    case 'bom':
      return 'text/csv';
    default:
      return 'text/plain';
  }
}
